/**
 * The quality scores — **M15 C-QUALITY, F17-FR-1/4/8**.
 *
 * ⚠️ **Derived, never re-scanned.** Every score below is computed from output the platform already
 * produced — M3's field profiles, M4's clusters, M2's run records — because
 * [cost-model §1](../../../docs/platform/cost-model.md) is clear that the hot registry is the one
 * layer whose cost tracks record count, and a monitoring feature that re-read staged Parquet on a
 * schedule would make the cheap thing expensive. It is also the accuracy argument: a score computed
 * from a *different* read than the findings it sits beside would disagree with them.
 *
 * ── Formulas are fixed, published, and shown on every score (Q33) ──────────────────────────────
 *
 * ⚠️ **The formula is the part customers dispute**, which is why fixing and publishing it matters
 * more than the threshold. A tenant-defined formula was rejected: it would make scores incomparable
 * across tenants *and across time*, destroying the trend that is the whole point of the feature. So
 * each score carries its own `formula` string — not as documentation, but as the thing a client reads
 * when they disagree with a number.
 *
 * ── `insufficient_data` is a first-class answer (F17-FR-8, F17-BR-5) ──────────────────────────
 *
 * ⚠️ **A rate over a tiny population is noise wearing a number's clothes.** One duplicate in four
 * records is 25%, and it is not a fact about data quality. Below the floor the score is
 * `insufficient_data` **with its population stated**, never a percentage and never zero — the same
 * rule the rest of this product applies to every not-knowable value.
 */

export type Dimension =
  | 'completeness'
  | 'format_conformity'
  | 'duplication_rate'
  | 'staleness'
  | 'pii_coverage'
  | 'cross_system_gap_rate';

export type ScoreState = 'scored' | 'insufficient_data' | 'not_applicable';

export interface Score {
  dimension: Dimension;
  /** ⚠️ `null` unless `state === 'scored'`. A state and a value that disagree is the bug this prevents. */
  value: number | null;
  state: ScoreState;
  /**
   * The population the rate was computed over — half of what a rate means.
   *
   * ⚠️ **`null` when the dimension does not APPLY, and that distinction was wrong until 2026-08-18.**
   * Every `not_applicable` score reported `population: 0` with `population_unit: 'records'`, so a single
   * response said both *0 records* and *240 records* about the same object at the same instant:
   * `completeness` (no identity fields mapped) sat beside `duplication_rate` (240 records, 100%). A
   * reader — or an agent — has no way to tell that the first `0` counts unmapped fields rather than
   * missing data.
   *
   * ⚠️ **The rule is "no population was CONSIDERED", not "the dimension does not apply"** — and the
   * difference is `staleness`. Four not-applicable dimensions never look at a population at all
   * (`completeness` and `pii_coverage` have no mapped fields, `format_conformity` no inferrable format,
   * `cross_system_gap_rate` no report), so `null` is the honest answer. `staleness` is not-applicable
   * for a different reason: **240 records were considered** and the provider reports no modification
   * time, so it keeps `240` and explains itself — *"this source does not report when a record was last
   * modified, which is not the same as none"*. Nulling that would throw away a true fact.
   *
   * ⚠️ **`insufficient_data` keeps its real number too**, deliberately: there the count IS the whole
   * finding (*"only 3 records, which is noise"*). And this repo's rule stands underneath all of it —
   * not-knowable is `null`, never `0`.
   */
  population: number | null;
  /**
   * What the population COUNTS.
   *
   * ⚠️ **Not decoration, and found in a browser rather than by a test.** Three of the six dimensions
   * are not rates over records: format conformity is over *values present*, PII coverage over
   * *fields profiled*, cross-system gaps over *entities in the reference set*. A screen that printed
   * "14 records" beside a 100% PII coverage figure computed over fourteen FIELDS is stating something
   * false about the size of the customer's data — and it is exactly the kind of false a reader has no
   * way to catch, because the number itself is right.
   */
  population_unit: 'records' | 'values' | 'fields' | 'entities';
  /** ⚠️ Published on every score. See the header: the formula is the disputed part. */
  formula: string;
  /** Which run produced the inputs (F17-FR-4). `null` only where the input is not run-scoped. */
  run_id: string | null;
  /** Present on `insufficient_data` and `not_applicable`. A state without a reason is a shrug. */
  reason?: string;
  /**
   * ⚠️ **Higher is better for some dimensions and worse for others**, and a scorecard that got this
   * wrong would colour a rising duplication rate green. Carried on the score rather than inferred
   * from the name by whichever screen renders it.
   */
  direction: 'higher_is_better' | 'lower_is_better';
}

/**
 * The smallest population a rate may be computed over.
 *
 * 🟡 **A starting figure, not a measurement** — the same status F07's thresholds carry. Thirty is the
 * conventional floor for treating a proportion as stable at all; it is stated as provisional on the
 * response rather than presented as derived, and real usage should move it.
 */
export const MIN_POPULATION = 30;

const insufficient = (
  dimension: Dimension,
  population: number,
  formula: string,
  direction: Score['direction'],
  unit: Score['population_unit'] = 'records'
): Score => ({
  dimension,
  value: null,
  state: 'insufficient_data',
  population,
  formula,
  population_unit: unit,
  run_id: null,
  reason: `Only ${population} records — a rate below ${MIN_POPULATION} is noise rather than a measurement, so no score is given.`,
  direction,
});

/* ── The six formulas, written once ───────────────────────────────────────────────────────── */

export const FORMULAS: Record<Dimension, string> = {
  completeness: 'mean over identity fields of (rows with a non-empty value ÷ rows considered)',
  format_conformity: 'mean over fields with an inferred format of (values matching that format ÷ values present)',
  /* Q33's exact wording, so the code and the discovery record cannot drift. */
  duplication_rate: 'records in a confirmed multi-member cluster ÷ total live records for that object',
  staleness: 'records whose source last modified them more than the staleness window ago ÷ total live records',
  pii_coverage: 'fields with a PII classification ÷ fields profiled',
  cross_system_gap_rate: 'entities absent from a named target system ÷ entities in the reference set',
};

export interface FieldInput {
  field: string;
  total: number;
  present: number;
  completeness: number;
  /** Rows whose value matched the inferred format. `null` where no format was inferred. */
  format_conforming: number | null;
  /** `'none'` counts as classified — deciding a field holds no PII IS a classification. */
  pii: string | null;
}

export interface ScoreInput {
  run_id: string | null;
  /** Field profiles from M3, for the completeness / format / PII dimensions. */
  fields: FieldInput[];
  /** Which fields identify a record. Completeness is scored over these, not over every column. */
  identity_fields: string[];
  /** Live (non-tombstoned) records for the object. The denominator of the rate dimensions. */
  live_records: number;
  /** Records inside a confirmed multi-member cluster. */
  clustered_records: number;
  /** Records whose source modified them longer ago than the window. `null` when unknown. */
  stale_records: number | null;
  staleness_window_days: number;
  /** Cross-system: entities in the reference set, and how many are absent from the target. */
  reference_entities: number | null;
  missing_entities: number | null;
}

const round = (n: number) => Math.round(n * 1000) / 10;

/**
 * Score one object.
 *
 * ⚠️ **Every dimension returns a `Score`, including the ones that could not be computed.** An absent
 * dimension would read as "we have nothing to say about your formats" when the truth is "no format
 * was inferrable in this dataset" — a different fact, and one a client can act on.
 */
export function scoreObject(input: ScoreInput): Score[] {
  const scores: Score[] = [];

  /* ── Completeness — over IDENTITY fields, not every column ─────────────────────────────────
     ⚠️ A dataset with forty sparse custom columns and a full email would score badly on a whole-row
     average while being perfectly matchable. Completeness that does not predict matchability is a
     number that punishes a client for having optional fields. */
  const identity = input.fields.filter((f) => input.identity_fields.includes(f.field));
  if (identity.length === 0) {
    scores.push({
      dimension: 'completeness',
      population_unit: 'records',
      value: null,
      state: 'not_applicable',
      population: null,
      formula: FORMULAS.completeness,
      run_id: input.run_id,
      reason: 'No identity fields are mapped for this object, so there is nothing completeness would predict.',
      direction: 'higher_is_better',
    });
  } else if (input.live_records < MIN_POPULATION) {
    scores.push(insufficient('completeness', input.live_records, FORMULAS.completeness, 'higher_is_better'));
  } else {
    const mean = identity.reduce((sum, f) => sum + f.completeness, 0) / identity.length;
    scores.push({
      dimension: 'completeness',
      population_unit: 'records',
      value: round(mean),
      state: 'scored',
      population: input.live_records,
      formula: FORMULAS.completeness,
      run_id: input.run_id,
      direction: 'higher_is_better',
    });
  }

  /* ── Format conformity — only over fields that HAVE an inferred format ─────────────────────
     ⚠️ Counting a free-text field as non-conforming would make a notes column look like a data
     defect. `not_applicable` where nothing was inferrable. */
  const formatted = input.fields.filter((f) => f.format_conforming !== null && f.present > 0);
  if (formatted.length === 0) {
    scores.push({
      dimension: 'format_conformity',
      population_unit: 'values',
      value: null,
      state: 'not_applicable',
      population: null,
      formula: FORMULAS.format_conformity,
      run_id: input.run_id,
      reason: 'No field in this object has an inferrable format (email, phone, URL), so there is nothing to conform to.',
      direction: 'higher_is_better',
    });
  } else {
    const mean = formatted.reduce((sum, f) => sum + (f.format_conforming! / f.present), 0) / formatted.length;
    scores.push({
      dimension: 'format_conformity',
      population_unit: 'values',
      value: round(mean),
      state: 'scored',
      population: formatted.reduce((sum, f) => sum + f.present, 0),
      formula: FORMULAS.format_conformity,
      run_id: input.run_id,
      direction: 'higher_is_better',
    });
  }

  /* ── Duplication rate — Q33's formula, verbatim ────────────────────────────────────────── */
  if (input.live_records < MIN_POPULATION) {
    scores.push(insufficient('duplication_rate', input.live_records, FORMULAS.duplication_rate, 'lower_is_better'));
  } else {
    scores.push({
      dimension: 'duplication_rate',
      population_unit: 'records',
      value: round(input.clustered_records / input.live_records),
      state: 'scored',
      population: input.live_records,
      formula: FORMULAS.duplication_rate,
      run_id: input.run_id,
      direction: 'lower_is_better',
    });
  }

  /* ── Staleness ─────────────────────────────────────────────────────────────────────────── */
  if (input.stale_records === null) {
    /* ⚠️ Not-knowable, not zero. A provider that does not report a modification time makes this
       unanswerable, and a 0% staleness score for it would be the most flattering possible lie. */
    scores.push({
      dimension: 'staleness',
      population_unit: 'records',
      value: null,
      state: 'not_applicable',
      population: input.live_records,
      formula: FORMULAS.staleness,
      run_id: input.run_id,
      reason: 'This source does not report when a record was last modified, so staleness cannot be computed — which is not the same as none.',
      direction: 'lower_is_better',
    });
  } else if (input.live_records < MIN_POPULATION) {
    scores.push(insufficient('staleness', input.live_records, FORMULAS.staleness, 'lower_is_better'));
  } else {
    scores.push({
      dimension: 'staleness',
      population_unit: 'records',
      value: round(input.stale_records / input.live_records),
      state: 'scored',
      population: input.live_records,
      formula: `${FORMULAS.staleness} (window: ${input.staleness_window_days} days)`,
      run_id: input.run_id,
      direction: 'lower_is_better',
    });
  }

  /* ── PII coverage ──────────────────────────────────────────────────────────────────────────
     ⚠️ `'none'` COUNTS as classified. Deciding a field holds no personal data is a classification and
     a useful one; treating it as unclassified would make a correctly-assessed dataset look unassessed. */
  if (input.fields.length === 0) {
    scores.push({
      dimension: 'pii_coverage',
      population_unit: 'fields',
      value: null,
      state: 'not_applicable',
      population: null,
      formula: FORMULAS.pii_coverage,
      run_id: input.run_id,
      reason: 'No fields have been profiled for this object yet.',
      direction: 'higher_is_better',
    });
  } else {
    const classified = input.fields.filter((f) => f.pii !== null).length;
    scores.push({
      dimension: 'pii_coverage',
      population_unit: 'fields',
      value: round(classified / input.fields.length),
      state: 'scored',
      population: input.fields.length,
      formula: FORMULAS.pii_coverage,
      run_id: input.run_id,
      direction: 'higher_is_better',
    });
  }

  /* ── Cross-system gap rate — Q33's formula, and `not_applicable` with ONE source ──────────── */
  if (input.reference_entities === null || input.missing_entities === null) {
    scores.push({
      dimension: 'cross_system_gap_rate',
      population_unit: 'entities',
      value: null,
      state: 'not_applicable',
      population: null,
      formula: FORMULAS.cross_system_gap_rate,
      run_id: input.run_id,
      reason: 'Only one system is connected for this object, so there is no second system for a record to be missing from.',
      direction: 'lower_is_better',
    });
  } else if (input.reference_entities < MIN_POPULATION) {
    scores.push(insufficient('cross_system_gap_rate', input.reference_entities, FORMULAS.cross_system_gap_rate, 'lower_is_better', 'entities'));
  } else {
    scores.push({
      dimension: 'cross_system_gap_rate',
      population_unit: 'entities',
      value: round(input.missing_entities / input.reference_entities),
      state: 'scored',
      population: input.reference_entities,
      formula: FORMULAS.cross_system_gap_rate,
      run_id: input.run_id,
      direction: 'lower_is_better',
    });
  }

  return scores;
}
